// Loan Application Types

export type BorrowerType = 'new' | 'returning';
export type BuildType = 'new_construction' | 'fix_and_flip';
export type CompanyType = 'corporation' | 'partnership' | 'llc' | 'other';

// Company Information (Page 1)
export interface CompanyInformation {
  companyName: string;
  companyType: CompanyType | '';
  businessAddress: string;
  businessCity: string;
  businessState: string;
  businessZip: string;
  taxId: string;
  formationDate: string;
  mobile: string;
  email: string;
  // Secondary Contact
  secondaryName?: string;
  secondaryPhone?: string;
  secondaryEmail?: string;
  secondaryAddress?: string;
  secondaryCity?: string;
  secondaryState?: string;
  secondaryZip?: string;
  // Shepherd's Finance Representative
  sfRep?: string;
}

// Owner/Guarantor Information (Page 2)
// Updated to match backend API structure
export interface OwnerInformation {
  id?: string; // Backend returns this
  fullName: string;
  title: string;
  ownershipPercent: number;
  ssn?: string; // Full 9-digit SSN (used in frontend)
  ssnLast4?: string; // Only last 4 digits
  ssnToken?: string; // Full SSN token (encrypted, from backend)
  dateOfBirth: string;
  phone: string;
  email: string;
  address1: string;
  city: string;
  state: string;
  zip: string;
  isGuarantor: boolean;
}

// Disclosure Questions (Page 3)
export interface DisclosureQuestions {
  bankruptcy: 'yes' | 'no' | '';
  legalActions: 'yes' | 'no' | '';
  judgements: 'yes' | 'no' | '';
  unpaidTaxes: 'yes' | 'no' | '';
  explanation?: string; // Added to match backend
  explanationFiles?: File[];
}

// Recently Built Home (for custom builders without material references)
export interface RecentlyBuiltHome {
  contractFile?: File[];
  customerName: string;
  customerPhone: string;
  address: string;
  projectAmount: number | '';
  dateCompleted: string;
}

// Builder Experience (Page 4)
export interface BuilderExperience {
  projectedHomesThisYear?: number;
  homesBuilt2025?: number;
  homesBuilt2024?: number;
  homesBuilt2023?: number;
  homesBuilt2022?: number;
  homesBuilt2021?: number;
  totalYearsExperience?: number;
  yearsAsOwner?: number;
  totalHomesBuilt?: number;
  specsPercentage?: number;
  presoldPercentage?: number;
  companyHistory?: CompanyHistory[];
}

export interface CompanyHistory {
  companyName: string;
  yearsFrom: number | '';
  yearsTo: number | '';
  position: string;
  ownershipPercentage: number | '';
  homesBuilt: number | '';
  priceRangeMin: number | '';
  priceRangeMax: number | '';
  specPercentage: number | '';
}

// Credit References (Page 5)
export interface CreditReference {
  companyName: string;
  contactName: string;
  phone: string;
  accountNumber?: string;
  creditLimit?: number;
  currentBalance?: number;
}

// Supplier Reference (for Credit References page)
export interface SupplierReference {
  supplierName: string;
  creditLimit: number | '';
  currentBalance: number | '';
  phone: string;
  email: string;
  fax?: string;
  dateOpened: string;
  address: string;
  companyNameOnAccount: string;
  accountNumber: string;
}

// Credit References Data (Page 5)
export interface CreditReferencesData {
  primarySupplier: SupplierReference;
  secondarySupplier: SupplierReference;
  concreteSupplier: SupplierReference;
  cabinetSupplier: SupplierReference;
}

// Project Information (Page 6)
export interface ProjectInformation {
  // Backend required fields
  projectName?: string;
  propertyCity?: string;
  propertyState?: string;
  propertyZip?: string;
  loanPurpose?: string;
  purchasePrice?: number | '';
  rehabBudget?: number | '';
  totalProjectCost?: number | '';
  requestedLoanAmount?: number | '';
  lotDeedAvailable?: 'yes' | 'no' | '';
  notes?: string;
  // Frontend fields (will be mapped to backend)
  legalDescription?: string;
  streetAddress?: string;
  lotPurchasedAtClosing?: 'yes' | 'no' | '';
  allWeatherRoads?: 'yes' | 'no' | '';
  ownLot?: 'yes' | 'no' | '';
  mortgagePayoff?: 'yes' | 'no' | '';
  constructionStarted?: 'yes' | 'no' | '';
  estimatedStartDate?: string;
  estimatedCompletionDate?: string;
}

// Acknowledgement Signature (Page 7)
export interface AcknowledgementSignature {
  name: string;
  date: string;
  signature: string;
}

// Acknowledgement Data (Page 7)
export interface AcknowledgementData {
  signatures: AcknowledgementSignature[];
  hearAboutUs: string;
  hearAboutUsDetail?: string;
}

// Complete Application State
export interface LoanApplication {
  // Borrower Type Selection
  borrowerType: BorrowerType | '';
  buildType: BuildType | '';
  builtFiveIn3Years: boolean | null;
  newBorrowerNoReason?: string;
  experiencedDetail?: string;

  // Company Ownership
  isOwnedByCompany: boolean;
  parentCompanyName: string;

  // Page Data
  companyInformation: CompanyInformation;
  owners: OwnerInformation[];
  disclosureQuestions: DisclosureQuestions;
  builderExperience: BuilderExperience;
  creditReferences: CreditReference[];
  creditReferencesData: CreditReferencesData;
  projectInformation: ProjectInformation;
  acknowledgement: AcknowledgementData;
  recentlyBuiltHomes: RecentlyBuiltHome[];

  // Application Metadata
  applicationId: string | null;
  currentPage: number;
  completedPages: number[];

  // File Uploads
  contractorLicense?: File[];
  companyResume?: File[];
  bankStatements?: File[];
  taxReturns?: File[];
  creditCardStatements?: File[];
  lotDeed?: File[];
}

// Initial State
export const initialCompanyInformation: CompanyInformation = {
  companyName: '',
  companyType: '',
  businessAddress: '',
  businessCity: '',
  businessState: '',
  businessZip: '',
  taxId: '',
  formationDate: '',
  mobile: '',
  email: '',
  secondaryName: '',
  secondaryPhone: '',
  secondaryEmail: '',
  secondaryAddress: '',
  secondaryCity: '',
  secondaryState: '',
  secondaryZip: '',
  sfRep: '',
};

export const initialOwnerInformation: OwnerInformation = {
  fullName: '',
  title: '',
  ownershipPercent: 0,
  ssn: '',
  ssnLast4: '',
  dateOfBirth: '',
  phone: '',
  email: '',
  address1: '',
  city: '',
  state: '',
  zip: '',
  isGuarantor: false,
};

export const initialDisclosureQuestions: DisclosureQuestions = {
  bankruptcy: '',
  legalActions: '',
  judgements: '',
  unpaidTaxes: '',
  explanationFiles: [],
};

export const initialCompanyHistory: CompanyHistory = {
  companyName: '',
  yearsFrom: '',
  yearsTo: '',
  position: '',
  ownershipPercentage: '',
  homesBuilt: '',
  priceRangeMin: '',
  priceRangeMax: '',
  specPercentage: '',
};

export const initialRecentlyBuiltHome: RecentlyBuiltHome = {
  contractFile: [],
  customerName: '',
  customerPhone: '',
  address: '',
  projectAmount: '',
  dateCompleted: '',
};

export const initialBuilderExperience: BuilderExperience = {
  projectedHomesThisYear: 0,
  homesBuilt2025: 0,
  homesBuilt2024: 0,
  homesBuilt2023: 0,
  homesBuilt2022: 0,
  homesBuilt2021: 0,
  totalYearsExperience: 0,
  yearsAsOwner: 0,
  totalHomesBuilt: 0,
  specsPercentage: 0,
  presoldPercentage: 0,
  companyHistory: [{ ...initialCompanyHistory }],
};

export const initialProjectInformation: ProjectInformation = {
  // Backend fields
  projectName: '',
  propertyCity: '',
  propertyState: '',
  propertyZip: '',
  loanPurpose: '',
  purchasePrice: '',
  rehabBudget: '',
  totalProjectCost: '',
  requestedLoanAmount: '',
  lotDeedAvailable: '',
  notes: '',
  // Frontend fields
  legalDescription: '',
  streetAddress: '',
  lotPurchasedAtClosing: '',
  allWeatherRoads: '',
  ownLot: '',
  mortgagePayoff: '',
  constructionStarted: '',
  estimatedStartDate: '',
  estimatedCompletionDate: '',
};

export const initialSupplierReference: SupplierReference = {
  supplierName: '',
  creditLimit: '',
  currentBalance: '',
  phone: '',
  email: '',
  fax: '',
  dateOpened: '',
  address: '',
  companyNameOnAccount: '',
  accountNumber: '',
};

export const initialCreditReferencesData: CreditReferencesData = {
  primarySupplier: { ...initialSupplierReference },
  secondarySupplier: { ...initialSupplierReference },
  concreteSupplier: { ...initialSupplierReference },
  cabinetSupplier: { ...initialSupplierReference },
};

export const initialAcknowledgementSignature: AcknowledgementSignature = {
  name: '',
  date: '',
  signature: '',
};

export const initialAcknowledgementData: AcknowledgementData = {
  signatures: [{ ...initialAcknowledgementSignature }],
  hearAboutUs: '',
  hearAboutUsDetail: '',
};

export const initialLoanApplication: LoanApplication = {
  borrowerType: '',
  buildType: '',
  builtFiveIn3Years: null,
  newBorrowerNoReason: '',
  experiencedDetail: '',
  isOwnedByCompany: false,
  parentCompanyName: '',
  companyInformation: initialCompanyInformation,
  owners: [initialOwnerInformation],
  disclosureQuestions: initialDisclosureQuestions,
  builderExperience: initialBuilderExperience,
  creditReferences: [],
  creditReferencesData: initialCreditReferencesData,
  projectInformation: initialProjectInformation,
  acknowledgement: initialAcknowledgementData,
  recentlyBuiltHomes: [],
  applicationId: null,
  currentPage: 0,
  completedPages: [],
};
